import classNames from 'classnames'
import React, { useId, useRef, useState } from 'react'
import CurrencyIcon from '@/components/currencyIcon/CurrencyIcon'
import { useCurrencies } from '@/hooks'
import NumberFlow from '@number-flow/react'
import { useEffect } from 'react'

const NumericInput = ({
  disabled,
  className,
  value,
  onChange,
  decimalScale,
  placeholder,
  locale = 'en-US',
  _animated = true,
  onAnimatedChange,
  labelInside = false,
  currencyId,
  prefix,
  suffix,
  max,
  testId,
  inputId,
  ariaLabel,
  valueClassName,
  align = 'left',
  size = 'default', // 'default' | 'display' — 'display' scales down as the number grows
}) => {
  const inputRef = useRef(null)
  // const defaultValue = useRef(value || 0)
  const [animated, setAnimated] = useState(_animated)
  const [, setShowCaret] = useState(true)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    setAnimated(_animated)
  }, [_animated])

  // Dynamic font size based on input length
  // Note: Using minimum 16px font-size to prevent iOS Safari zoom
  const getDynamicFontSize = () => {
    const valueStr = value?.toString() || ''
    const length = valueStr.length

    if (size === 'display') {
      if (length > 11) return 'text-lg'
      if (length > 8) return 'text-2xl'
      if (length > 6) return 'text-[28px]'
      return 'text-[32px]'
    }

    if (labelInside) {
      if (length > 7) return 'text-base' // 16px minimum
      if (length > 5) return 'text-base' // 16px minimum
      return 'text-base' // 16px minimum
    } else {
      if (length > 7) return 'text-base' // 16px minimum
      if (length > 5) return 'text-lg'
      return '!text-xl'
    }
  }

  const handleInput = event => {
    setAnimated(false)
    onAnimatedChange?.(false)

    // Get the raw input value
    let inputValue = event.target.value

    // Handle empty input separately
    if (inputValue === '') {
      onChange('')
      return
    }

    // Allow for both comma and period as decimal separators
    // Replace any comma with a period internally (for JavaScript processing)
    const normalizedInput = inputValue.replace(',', '.')

    // Allow just a decimal separator to be entered (either . or ,)
    if (inputValue === '.' || inputValue === ',') {
      onChange('0.')
      return
    }

    // Make regex accept both formats but normalize for internal processing
    const decimalRegex = new RegExp(`^\\d*([\\.,]\\d{0,${decimalScale}})?$`)

    if (!decimalRegex.test(inputValue)) {
      return
    }

    // Clamp to max if provided. Only rewrite the string when actually clamping —
    // round-tripping every keystroke through parseFloat drops a trailing separator.
    const numericValue = parseFloat(normalizedInput)
    if (max !== undefined && max !== null && !isNaN(numericValue) && numericValue > max) {
      onChange(max.toString())
      return
    }

    // For internal value storage, we always use period as decimal separator
    onChange(normalizedInput)
  }

  // Function to handle displaying the value with decimal point
  const getDisplayValue = () => {
    if (!value || value === '') return 0

    // If the value ends with a decimal point, display it as entered
    if (value.toString().endsWith('.')) {
      // Add a small value after the decimal to make NumberFlow display the decimal point
      return parseFloat(value + '01')
    }

    // If the value has multiple zeros after decimal (e.g., "0.0" or "0.00")
    if (value.toString().includes('.') && value.toString().split('.')[1].length > 0) {
      // Add a small value to make NumberFlow display the correct number of decimal places
      // return parseFloat(value + '1')
    }

    return parseFloat(value) || 0
  }

  return (
    <div className="relative grid items-center justify-items-center text-center [grid-template-areas:'overlap'] *:[grid-area:overlap] w-full max-w-full overflow-hidden">
      <input
        type="text"
        id={inputId}
        data-testid={testId}
        aria-label={ariaLabel || undefined}
        ref={inputRef}
        disabled={disabled}
        className={classNames(
          'spin-hide w-full max-w-full bg-transparent font-bold outline-none overflow-hidden text-ellipsis',
          size === 'display' ? 'h-[38px]' : 'h-[30px]',
          getDynamicFontSize(),
          align === 'center' ? 'text-center' : 'text-left',
          {
            'text-transparent': !isFocused, // Hide text when not focused
            // Force 16px font-size on iOS to prevent zoom. The display tiers never go below
            // 16px, so they can keep their own size while typing.
            '!text-base': size !== 'display',
          },
          className
        )}
        style={{ fontKerning: 'none' }}
        inputMode="decimal"
        pattern="[0-9.,]*"
        value={value || ''}
        onInput={handleInput}
        placeholder={placeholder}
        onFocus={e => {
          e.target.select()
          setIsFocused(true)
        }}
        onBlur={() => setIsFocused(false)}
      />

      <NumberFlow
        value={getDisplayValue()}
        locales={locale}
        format={{ useGrouping: false, maximumFractionDigits: decimalScale }}
        aria-hidden="true"
        animated={animated}
        onAnimationsStart={() => setShowCaret(false)}
        onAnimationsFinish={() => setShowCaret(true)}
        className={classNames(
          'pointer-events-none w-full max-w-full font-bold overflow-hidden text-ellipsis',
          align === 'center' ? 'text-center' : 'text-left',
          getDynamicFontSize(),
          valueClassName,
          {
            'opacity-0': isFocused, // Hide NumberFlow when input is focused
          }
        )}
        willChange
        prefix={prefix}
        suffix={suffix?.toLowerCase()}
      />
    </div>
  )
}

const AmountChips = ({ onSelect, chipSet = 'buy', onPointerDown, currencyId }) => {
  const currencies = useCurrencies().currencies
  const currency = currencies[currencyId]

  const chips =
    chipSet === 'sharesIncrement'
      ? [
          { value: 10, label: '+10' },
          { value: 100, label: '+100' },
          { value: 'max', label: 'Max' },
        ]
      : chipSet === 'shares'
      ? [
          { value: 0.25, label: '25%' },
          { value: 0.5, label: '50%' },
          { value: 'max', label: 'Max' },
        ]
      : chipSet === 'buy'
      ? [
          {
            value: currency?.min_bet_amount_increase_step || 10,
            label: '+' + (currency?.min_bet_amount_increase_step || 10),
          },
          {
            value: currency?.max_bet_amount_increase_step || 100,
            label: '+' + (currency?.max_bet_amount_increase_step || 100),
          },
          { value: 'max', label: 'Max' },
        ]
      : [
          { value: 0.25, label: '25%' },
          { value: 0.5, label: '50%' },
          { value: 1, label: '100%' },
        ]

  return (
    <div className="flex gap-1">
      {chips.map(chip => (
        <button
          key={chip.label}
          onPointerDown={onPointerDown}
          onClick={() => onSelect(chip.value)}
          className="px-2 py-1.5 bg-surface-3 cursor-pointer hover:bg-surface-3/50 rounded-[var(--radius-sm)] text-xs font-medium text-Neutral-4 transition-colors">
          {chip.label}
        </button>
      ))}
    </div>
  )
}

const DecrementButton = ({ onClick, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    className="p-1 hover:bg-surface-3 rounded transition-colors">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.33325 8H12.6666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </button>
)

const IncrementButton = ({ onClick, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    className="p-1 hover:bg-surface-3 rounded transition-colors">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 3.33337V12.6667M3.33334 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </button>
)

const LimitButtons = ({ onIncrement, onDecrement }) => {
  return (
    <div className="flex gap-4">
      <DecrementButton onClick={onDecrement} />
      <IncrementButton onClick={onIncrement} />
    </div>
  )
}

const PlaceBetInput = ({
  disabled,
  value,
  onChange,
  decimalScale = 2,
  placeholder = '0,00',
  onChipSelect,
  chipSet = 'buy',
  currencyId,
  hasError,
  locale = 'en-US',
  mode = 'market', // 'market' or 'limit'
  label = '',
  step = 1,
  labelInside = false,
  noCurrency = false,
  prefix,
  suffix,
  max,
  testId,
  // 'box'     — bordered field (default)
  // 'row'     — label left, stepper wrapped around the value on the right
  // 'stacked' — label above a large value, chips on the right
  // 'row'/'stacked' are borderless: the surrounding card owns the border.
  variant = 'box',
  helperText,
}) => {
  const currencies = useCurrencies().currencies
  const currency = currencies[currencyId]
  const [animated, setAnimated] = useState(true)
  const inputRef = useRef(null)
  const inputId = `place_bet_input_${useId()}`

  const handlePointerDown = event => {
    setAnimated(true)
    if (event.pointerType === 'mouse') {
      event?.preventDefault()
      inputRef.current?.focus()
    }
  }

  const handleIncrement = () => {
    const currentValue = parseFloat(value || 0)
    let newValue = currentValue + step
    if (max !== undefined && max !== null && !isNaN(newValue)) {
      newValue = Math.min(newValue, max)
    }
    // Format to avoid unnecessary decimals and then parse to remove trailing zeros
    const formattedValue = parseFloat(newValue.toFixed(decimalScale)).toString()
    onChange(formattedValue)
  }

  const handleDecrement = () => {
    const currentValue = parseFloat(value || 0)
    const newValue = Math.max(0, currentValue - step)
    // Format to avoid unnecessary decimals and then parse to remove trailing zeros
    const formattedValue = parseFloat(newValue.toFixed(decimalScale)).toString()
    onChange(formattedValue)
  }

  const handleChipSelect = chipValue => {
    if (chipSet === 'sharesIncrement') {
      // These chips are increments: each click adds to the current amount.
      if (typeof chipValue === 'number') {
        let newValue = parseFloat(value || 0) + chipValue
        if (max !== undefined && max !== null) {
          newValue = Math.min(newValue, max)
        }
        onChange(parseFloat(newValue.toFixed(decimalScale)).toString())
        return
      }
      onChipSelect?.(chipValue)
      return
    }

    if (chipSet === 'sell' || chipSet === 'shares') {
      onChipSelect?.(chipValue)
      return
    }

    if (chipValue === 'max' && max !== undefined && max !== null) {
      onChipSelect?.(max)
      onChange(max.toString())
    } else if (typeof chipValue === 'number') {
      let newValue = parseFloat(value || 0) + chipValue * currency.display_ratio
      if (max !== undefined && max !== null) {
        newValue = Math.min(newValue, max)
      }
      onChipSelect?.(newValue)
      onChange(newValue.toString())
    } else {
      onChipSelect?.(chipValue)
    }
  }

  if (variant === 'row') {
    return (
      <div className="flex items-center justify-between gap-3 p-4">
        <label htmlFor={inputId} className="text-sm text-Neutral-4">
          {label}
        </label>
        <div className="flex flex-shrink-0 items-center gap-3">
          <DecrementButton onClick={handleDecrement} ariaLabel={`${label} -`} />
          <div className="w-[72px]">
            <NumericInput
              disabled={disabled}
              value={value}
              onChange={onChange}
              decimalScale={decimalScale}
              placeholder={placeholder}
              locale={locale}
              _animated={animated}
              onAnimatedChange={setAnimated}
              prefix={prefix}
              suffix={suffix}
              max={max}
              testId={testId}
              inputId={inputId}
              ariaLabel={label}
              align="center"
              labelInside
              valueClassName="text-Neutral-6"
            />
          </div>
          <IncrementButton onClick={handleIncrement} ariaLabel={`${label} +`} />
        </div>
      </div>
    )
  }

  if (variant === 'stacked') {
    return (
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-3">
          <label htmlFor={inputId} className="text-sm text-Neutral-4">
            {label}
          </label>
          {helperText && <div className="flex-shrink-0 text-xs text-Neutral-4">{helperText}</div>}
        </div>
        <div className="flex items-end justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <NumericInput
              disabled={disabled}
              value={value}
              onChange={onChange}
              decimalScale={decimalScale}
              placeholder={placeholder}
              locale={locale}
              _animated={animated}
              onAnimatedChange={setAnimated}
              prefix={prefix}
              suffix={suffix}
              max={max}
              testId={testId}
              inputId={inputId}
              ariaLabel={label}
              size="display"
              valueClassName={classNames('leading-none', {
                'text-Neutral-4': !parseFloat(value),
                'text-Neutral-6': Boolean(parseFloat(value)),
              })}
            />
          </div>
          {onChipSelect && (
            <div className="flex-shrink-0 pb-1">
              <AmountChips
                onSelect={handleChipSelect}
                chipSet={chipSet}
                onPointerDown={handlePointerDown}
                currencyId={currencyId}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {label && !labelInside && (
        <label htmlFor={inputId} className="text-sm text-Neutral-4 mb-1 capitalize">
          {label}
        </label>
      )}
      <div
        className={classNames(
          'relative h-14 flex gap-2 items-center justify-between bg-surface-2 transition duration-200 rounded-[var(--radius-sm)] !p-3 w-full',
          'border border-solid border-Neutral-3 hover:border-Neutral-6 focus-within:!border-[var(--color-brand-04)] ',
          {
            'border-negative-4 hover:border-negative-4 focus-within:border-negative-4 focus-within:ring-negative-4':
              hasError,
          }
        )}>
        {!noCurrency && (
          <div className="w-4 flex-shrink-0">
            <CurrencyIcon currency={currency} width={16} height={16} />
          </div>
        )}

        <div className="flex flex-col flex-1 min-w-0 max-w-[200px]">
          {labelInside && (
            <label htmlFor={inputId} className="text-xs text-Neutral-4">
              {label}
            </label>
          )}

          <NumericInput
            disabled={disabled}
            value={value}
            onChange={onChange}
            decimalScale={decimalScale}
            placeholder={placeholder}
            locale={locale}
            _animated={animated}
            onAnimatedChange={setAnimated}
            labelInside={labelInside}
            prefix={prefix}
            suffix={suffix}
            max={max}
            testId={testId}
            inputId={inputId}
            ariaLabel={label}
          />
        </div>

        <div className="flex-shrink-0">
          {mode === 'market' ? (
            <AmountChips
              onSelect={handleChipSelect}
              chipSet={chipSet}
              onPointerDown={handlePointerDown}
              currencyId={currencyId}
            />
          ) : (
            <LimitButtons onIncrement={handleIncrement} onDecrement={handleDecrement} />
          )}
        </div>
      </div>
    </div>
  )
}

export default PlaceBetInput
